import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "392",
  "slug": "what-is-sentinel-2-satellite",
  "title": "What Is Sentinel-2? The Satellites That Photograph Earth Every Few Days",
  "excerpt": "Sentinel-2 is a pair of European satellites that map the planet's land and coasts in sharp, free, regularly updated imagery. Here's how they work and why they matter.",
  "date": "2026-06-10",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYXRlbGxpdGUlMjBvcmJpdCUyMGVhcnRofGVufDF8MHx8fDE3ODEwNzkyMTV8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "Sentinel-2 is a multi-satellite mission run by the European Space Agency that images all of Earth's land surface every few days at up to 10-metre resolution, and gives the data away for free.",
  "content": [
    {
      "type": "paragraph",
      "text": "Every time you see a fresh satellite picture of a flooded river, a burned hillside, or a field changing colour through the season, there's a good chance it came from a mission most people have never heard of: Sentinel-2. It is one of the quiet workhorses of modern Earth observation, and it has changed what ordinary people, not just space agencies, can do with a view from orbit."
    },
    {
      "type": "heading",
      "text": "What Sentinel-2 Actually Is"
    },
    {
      "type": "paragraph",
      "text": "Sentinel-2 is part of Copernicus, the European Union's flagship Earth-observation programme, and it is operated by the European Space Agency. Rather than a single spacecraft, it is a small fleet of identical satellites flying in the same orbit but spaced apart. Sentinel-2A launched in 2015 and Sentinel-2B in 2017, with a third, Sentinel-2C, added later to keep the mission running without a gap. They circle the planet in a near-polar, sun-synchronous orbit roughly 786 kilometres up, which means they pass over each spot at about the same local time every visit, keeping lighting consistent from one image to the next."
    },
    {
      "type": "heading",
      "text": "How Often It Sees Your Town"
    },
    {
      "type": "paragraph",
      "text": "The reason Sentinel-2 is so useful is its revisit rate. A single satellite would take about ten days to re-photograph the same point. With two satellites working together, that drops to roughly five days at the equator, and even faster at higher latitudes where the orbital tracks overlap. Each pass sweeps a swath about 290 kilometres wide, so the satellites build up a complete, refreshed picture of the planet's land and coastal waters on a steady cycle. That cadence is what lets scientists watch a wildfire scar appear, a crop ripen, or a glacier retreat almost in real time."
    },
    {
      "type": "heading",
      "text": "What 10-Metre Resolution Means"
    },
    {
      "type": "paragraph",
      "text": "Sentinel-2's sharpest bands resolve detail down to about 10 metres per pixel, with other bands at 20 and 60 metres. Ten metres is plenty to pick out fields, forests, rivers, city blocks, and coastlines, which is exactly what the mission is designed for. It is not built to spot individual cars or people, and that is by design: this is a land-monitoring instrument, not a spy camera. The trade-off for that moderate resolution is enormous coverage and frequent updates."
    },
    {
      "type": "heading",
      "text": "Seeing Beyond Visible Light"
    },
    {
      "type": "paragraph",
      "text": "What makes Sentinel-2 powerful is that it is multispectral. It captures 13 separate spectral bands, including several in the near-infrared and shortwave infrared that the human eye cannot see. Plants reflect near-infrared light strongly, so by comparing those bands analysts can measure vegetation health, distinguish crops from weeds, and detect drought stress long before it is visible to us. Other bands are tuned to pick out water, snow, and even thin cloud."
    },
    {
      "type": "list",
      "items": [
        "Tracking deforestation and illegal logging in near real time",
        "Monitoring crop health and forecasting harvests",
        "Mapping floods, wildfire scars, and the spread of algae blooms",
        "Measuring glacier retreat and seasonal snow cover",
        "Watching cities expand and farmland change use over the years"
      ]
    },
    {
      "type": "heading",
      "text": "Why Free and Open Data Changed Everything"
    },
    {
      "type": "paragraph",
      "text": "Perhaps the most important thing about Sentinel-2 is not the hardware at all, it is the licence. The imagery is free and open to anyone, from a government agency to a curious student. Before programmes like this, high-quality satellite imagery was expensive and locked behind commercial contracts. Opening it up has fuelled a wave of apps, research, and tools that treat a view from orbit as a normal raw material rather than a luxury."
    },
    {
      "type": "paragraph",
      "text": "When opening a Sentinel-2 image, check its date and band combination before interpreting the colours. A false-colour composite and a natural-colour view of the same place answer different questions."
    }
  ]
};
